<template>
    <div class="px-2 mb-20 w-full">
        <div id="title">
            <p class="text-black dark:text-white text-justify">
                {{ $t("indexPage.title") }}
            </p>
            <div
                class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 mt-4 items-center justify-items-center"
            >
                <a
                    href="#posix-to-date"
                    class="no-underline hover:underline text-primary-primary text-center py-2 px-3 rounded-lg hover:bg-primary-primary hover:text-white transition-all duration-300 w-full"
                >
                    {{ $t("indexPage.navigation.posixToDate") }}
                </a>
                <a
                    href="#date-to-posix"
                    class="no-underline hover:underline text-primary-primary text-center py-2 px-3 rounded-lg hover:bg-primary-primary hover:text-white transition-all duration-300 w-full"
                >
                    {{ $t("indexPage.navigation.dateToPosix") }}
                </a>
                <a
                    href="#json-posix-to-date"
                    class="no-underline hover:underline text-primary-primary text-center py-2 px-3 rounded-lg hover:bg-primary-primary hover:text-white transition-all duration-300 w-full"
                >
                    {{ $t("indexPage.navigation.jsonPosixToDate") }}
                </a>
                <a
                    href="#json-date-to-posix"
                    class="no-underline hover:underline text-primary-primary text-center py-2 px-3 rounded-lg hover:bg-primary-primary hover:text-white transition-all duration-300 w-full"
                >
                    {{ $t("indexPage.navigation.jsonDateToPosix") }}
                </a>
                <a
                    href="#whats-different"
                    class="no-underline hover:underline text-primary-primary text-center py-2 px-3 rounded-lg hover:bg-primary-primary hover:text-white transition-all duration-300 w-full"
                >
                    {{ $t("indexPage.navigation.whatsDifferent") }}
                </a>
                <a
                    href="#benefits-unix"
                    class="no-underline hover:underline text-primary-primary text-center py-2 px-3 rounded-lg hover:bg-primary-primary hover:text-white transition-all duration-300 w-full"
                >
                    {{ $t("indexPage.navigation.benefitsUnix") }}
                </a>
                <div
                    class="flex justify-center w-full col-span-2 md:col-span-3 lg:col-span-4 xl:col-span-6"
                >
                    <change-theme class="mx-2" />
                </div>
            </div>
        </div>
        <div id="posix-to-date" class="mt-8 pt-4">
            <h2 class="text-xl font-semibold dark:text-white">
                {{ $t("indexPage.sections.posixToDate.title") }}
            </h2>
            <div class="mt-4">
                <span class="text-black dark:text-white">
                    {{ $t("indexPage.sections.posixToDate.description") }}
                </span>
            </div>
            <div class="mt-6 grid lg:grid-cols-2 gap-3 grid-cols-1">
                <div
                    class="border border-primary-emphasis rounded"
                    v-for="instance in posixInstances"
                    :key="`posix${instance.id}`"
                >
                    <posix-to-date-converter :id="`${instance.id}`" />
                </div>

                <div class="h-full flex items-center justify-center min-h-40">
                    <button
                        @click="newPosix"
                        class="border h-full w-full border-primary-emphasis hover:bg-primary-emphasis hover:text-white text-primary-emphasis dark:text-white font-bold py-2 px-4 rounded-lg text-xl"
                    >
                        {{ $t("indexPage.sections.posixToDate.newConversion") }}
                        <br />
                        ➕
                    </button>
                </div>
            </div>
        </div>
        <div id="date-to-posix" class="mt-8 pt-4">
            <h2 class="text-xl font-semibold dark:text-white">
                {{ $t("indexPage.sections.dateToPosix.title") }}
            </h2>
            <div class="mt-4">
                <span class="text-black dark:text-white">
                    {{ $t("indexPage.sections.dateToPosix.description") }}
                </span>
            </div>

            <div
                class="mt-6 grid lg:grid-cols-3 md:grid-cols-2 gap-3 grid-cols-1"
            >
                <div
                    class="border border-primary-emphasis rounded px-2 py-2"
                    v-for="instance in datesInstances"
                    :key="`dates-${instance.id}`"
                >
                    <date-to-posix-converter :id="`${instance.id}`" />
                </div>
                <div class="h-full flex items-center justify-center min-h-28">
                    <button
                        @click="newDate"
                        class="border h-full w-full border-primary-emphasis hover:bg-primary-emphasis hover:text-white text-primary-emphasis dark:text-white font-bold py-2 px-4 rounded-lg text-xl"
                    >
                        {{ $t("indexPage.sections.dateToPosix.newConversion") }}
                        <br />
                        ➕
                    </button>
                </div>
            </div>
        </div>
        <div id="json-posix-to-date" class="mt-8 pt-4">
            <h2 class="text-xl font-semibold dark:text-white">
                {{ $t("indexPage.sections.jsonPosixToDate.title") }}
            </h2>
            <div class="mt-4">
                <p class="text-black dark:text-white text-justify">
                    {{ $t("indexPage.sections.jsonPosixToDate.description") }}
                </p>
            </div>
            <posix-to-date-json-converter class="mt-4" />
        </div>

        <div id="json-date-to-posix" class="mt-8 pt-4">
            <h2 class="text-xl font-semibold dark:text-white">
                {{ $t("indexPage.sections.jsonPosixToDate.title") }}
            </h2>
            <div class="mt-4">
                <p class="text-black dark:text-white text-justify">
                    {{ $t("indexPage.sections.jsonPosixToDate.description") }}
                </p>
            </div>
            <date-to-posix-json-converter class="mt-4" />
        </div>

        <div id="whats-different" class="mt-8 pt-4">
            <h2 class="text-xl font-semibold dark:text-white">
                {{ $t("indexPage.sections.whatsDifferent.title") }}
            </h2>
            <ul class="list-disc pl-6 pr-2 my-6">
                <li class="mb-4">
                    <p class="text-justify">
                        {{ $t("indexPage.sections.whatsDifferent.features.1") }}
                    </p>
                </li>
                <li class="mb-4">
                    <p class="text-justify">
                        {{ $t("indexPage.sections.whatsDifferent.features.2") }}
                    </p>
                </li>
                <li class="mb-4">
                    <p class="text-justify">
                        {{ $t("indexPage.sections.whatsDifferent.features.3") }}
                    </p>
                </li>
                <li class="mb-4">
                    <p class="text-justify">
                        {{ $t("indexPage.sections.whatsDifferent.features.4") }}
                    </p>
                </li>
                <li class="mb-4">
                    <p class="text-justify">
                        {{ $t("indexPage.sections.whatsDifferent.features.5") }}
                    </p>
                </li>
            </ul>
        </div>
        <div id="benefits-unix" class="mt-8 pt-4">
            <h2 class="text-xl font-semibold dark:text-white">
                {{ $t("indexPage.sections.benefitsUnix.title") }}
            </h2>
            <ul class="list-disc pl-6 pr-2 my-6">
                <li class="mb-4">
                    <strong class="text-lg">
                        {{
                            $t(
                                "indexPage.sections.benefitsUnix.benefits.1.title"
                            )
                        }}
                    </strong>
                    <p class="text-justify">
                        {{
                            $t(
                                "indexPage.sections.benefitsUnix.benefits.1.description"
                            )
                        }}
                    </p>
                </li>
                <li class="mb-4">
                    <strong class="text-lg">
                        {{
                            $t(
                                "indexPage.sections.benefitsUnix.benefits.2.title"
                            )
                        }}
                    </strong>
                    <p class="text-justify">
                        {{
                            $t(
                                "indexPage.sections.benefitsUnix.benefits.2.description"
                            )
                        }}
                    </p>
                </li>
                <li class="mb-4">
                    <strong class="text-lg">
                        {{
                            $t(
                                "indexPage.sections.benefitsUnix.benefits.3.title"
                            )
                        }}
                    </strong>
                    <p class="text-justify">
                        {{
                            $t(
                                "indexPage.sections.benefitsUnix.benefits.3.description"
                            )
                        }}
                    </p>
                </li>
                <li class="mb-4">
                    <strong class="text-lg">
                        {{
                            $t(
                                "indexPage.sections.benefitsUnix.benefits.4.title"
                            )
                        }}
                    </strong>
                    <p class="text-justify">
                        {{
                            $t(
                                "indexPage.sections.benefitsUnix.benefits.4.description"
                            )
                        }}
                    </p>
                </li>
                <li class="mb-4">
                    <strong class="text-lg">
                        {{
                            $t(
                                "indexPage.sections.benefitsUnix.benefits.5.title"
                            )
                        }}
                    </strong>
                    <p class="text-justify">
                        {{
                            $t(
                                "indexPage.sections.benefitsUnix.benefits.5.description"
                            )
                        }}
                    </p>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import { reactive, toRefs } from "vue";
import { useI18n } from "vue-i18n";
import { useMeta } from "vue-meta";

import DateToPosixConverter from "@/components/dates/DateToPosixConverter.vue";
import DateToPosixJsonConverter from "@/components/dates/DateToPosixJsonConverter.vue";
import PosixToDateConverter from "@/components/dates/PosixToDateConverter.vue";
import PosixToDateJsonConverter from "@/components/dates/PosixToDateJsonConverter.vue";
import ChangeTheme from "@/components/utils/ChangeTheme.vue";

export default {
    name: "IndexPage",
    components: {
        PosixToDateConverter,
        ChangeTheme,
        DateToPosixConverter,
        PosixToDateJsonConverter,
        DateToPosixJsonConverter
    },
    setup() {
        const { locale, t } = useI18n();

        useMeta({
            title: t("indexPage.meta.title"),
            meta: [
                {
                    name: "description",
                    content: t("indexPage.meta.description")
                },
                {
                    name: "keywords",
                    content: t("indexPage.meta.keywords")
                },
                { name: "robots", content: "index, follow" },
                {
                    property: "og:title",
                    content: t("indexPage.meta.title")
                },
                {
                    property: "og:description",
                    content: t("indexPage.meta.description")
                },
                { property: "og:type", content: "website" },
                { property: "og:url", content: "https://posix.softool.co" },
                {
                    property: "og:image",
                    content: "https://posix.softool.co/img/index.jpg"
                },
                {
                    property: "og:image:secure_url",
                    content: "https://posix.softool.co/img/index.jpg"
                },
                { property: "og:image:height", content: "627" },
                { property: "og:image:width", content: "1200" },
                {
                    property: "og:image:alt",
                    content: t("indexPage.meta.ogImageAlt")
                },
                { property: "og:image:type", content: "image/jpg" }
            ],
            htmlAttrs: { lang: locale.value, amp: true }
        });

        const state = reactive({
            posixInstances: [{ id: 1, name: "Default" }],
            datesInstances: [{ id: 1, name: "", posix: 0 }]
        });
        const newPosix = () => {
            const lastPosix = state.posixInstances.at(-1);
            const newId = lastPosix.id + 1;
            state.posixInstances.push({
                id: newId,
                name: `New Posix ${newId}`
            });
        };

        const newDate = () => {
            const lastDate = state.datesInstances.at(-1);
            const newId = lastDate.id + 1;
            state.datesInstances.push({
                id: newId,
                name: "New Date"
            });
        };

        return {
            ...toRefs(state),
            newPosix,
            newDate
        };
    }
};
</script>
